// Centralized HTTP client for the Humanteq API.
//
// Replaces the scattered `fetch()` calls and the global `window.fetch` wrapper
// from the old app.js. A single place owns: base URL, auth header injection,
// and 401 → logout handling.

import { useAuthStore } from '../store/authStore'
import { useMessagingStore } from '../store/messagingStore'
import { useChatwootStore } from '../store/chatwootStore'
import { useOnboardingStore } from '../store/onboardingStore'
import * as metrica from '../lib/metrica'
import { API_BASE } from '../lib/platform'
import type { ApiErrorBody, ApiErrorDetail } from '../types/api'
import { disablePushDevice } from '../lib/pushDevice'

// Default HTTP timeout (original aborted sends at 30s — messaging.js:86-100).
const REQUEST_TIMEOUT_MS = 30000

/**
 * Coerce a FastAPI error `detail` into a plain string. `detail` may be a string,
 * an object (`{message}` / `{reason}`), or — on 422 validation errors — an ARRAY
 * of `{msg, loc}` objects. Rendering that array in JSX throws "Objects are not
 * valid as a React child", so always funnel error detail through this.
 */
function detailToString(detail: ApiErrorDetail | undefined): string | null {
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    const msgs = detail.map((d) => d?.msg || d?.message).filter(Boolean)
    return msgs.length ? msgs.join('. ') : null
  }
  if (detail && typeof detail === 'object') return detail.message || detail.reason || null
  return null
}

// Safe error text for screens: never returns a non-string.
export function messageFromError(err: unknown, fallback = 'Что-то пошло не так'): string {
  const e = err as { body?: ApiErrorBody; message?: string } | null | undefined
  return detailToString(e?.body?.detail) || e?.message || fallback
}

/**
 * Thrown for any non-2xx response. Carries the parsed body (when JSON) and the
 * HTTP status so callers / React Query can branch on it (e.g. 402 → paywall).
 */
export class ApiError extends Error {
  status: number
  body: ApiErrorBody | null

  constructor(status: number, body: ApiErrorBody | null) {
    // Never let a non-string detail (e.g. a 422 array) become the message.
    super(detailToString(body?.detail) || body?.message || `HTTP ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

interface RequestOptions {
  method?: string
  body?: unknown
  auth?: boolean
  headers?: Record<string, string>
  timeout?: number
  // Whether a 401 should trigger the global "session expired → logout" flow.
  // Default true. Set false for endpoints where a 401 is a business error, not a
  // stale token — e.g. change-password (PUT /profile) returns 401 "Current
  // password is incorrect", which must show inline, not log the user out.
  logoutOn401?: boolean
  signal?: AbortSignal
}

async function request<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = true, headers = {}, timeout = REQUEST_TIMEOUT_MS, logoutOn401 = true, signal } = opts
  const generation = useAuthStore.getState().sessionGeneration
  const sameSession = () => useAuthStore.getState().sessionGeneration === generation
  const finalHeaders: Record<string, string> = { ...headers }
  let payload = body as BodyInit | undefined

  if (body !== undefined && !(body instanceof FormData)) {
    finalHeaders['Content-Type'] = 'application/json'
    payload = JSON.stringify(body)
  }

  if (auth) {
    const token = useAuthStore.getState().accessToken
    if (token) finalHeaders['Authorization'] = `Bearer ${token}`
  }

  // Abort the request after `timeout` ms so a hung network never leaves the UI
  // stuck (e.g. a permanent "typing…" indicator). Surfaces as ApiError(408).
  const controller = new AbortController()
  const abortFromCaller = () => controller.abort()
  if (signal?.aborted) controller.abort()
  else signal?.addEventListener('abort', abortFromCaller, { once: true })
  const timer = setTimeout(() => controller.abort(), timeout)
  let res: Response
  try {
    res = await fetch(`${API_BASE}${path}`, { method, headers: finalHeaders, body: payload, signal: controller.signal })
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') {
      throw new ApiError(408, { detail: 'Превышено время ожидания ответа. Попробуйте ещё раз.' })
    }
    throw err
  } finally {
    clearTimeout(timer)
    signal?.removeEventListener('abort', abortFromCaller)
  }

  // Mirror the old global 401 interceptor: any expired/invalid token logs out.
  // Match useAuth().logout's privacy cleanup — wipe the previous user's
  // persisted transcript + analytics identity so an expired token on a shared
  // device doesn't leave their chat/identity for the next account.
  if (res.status === 401 && auth && logoutOn401 && sameSession()) {
    const expiredUserId = useAuthStore.getState().userId
    if (expiredUserId) {
      void disablePushDevice(expiredUserId, undefined, { rollbackOnFailure: false })
    }
    useAuthStore.getState().logout()
    useMessagingStore.getState().clear()
    useMessagingStore.persist.clearStorage()
    useChatwootStore.getState().reset()
    metrica.resetUserId()
  }

  let parsed: unknown = null
  const contentType = res.headers.get('content-type') || ''
  if (contentType.includes('application/json')) {
    parsed = await res.json().catch(() => null)
  }

  // 409 onboarding_required: backend gate (bd-1am) says this user hasn't done
  // onboarding. Stale localStorage cache or gate race let them reach chat —
  // self-heal: invalidate the cache + hard navigate to /onboarding. Hard
  // navigation (not client-side) so AppShell unmounts and the gate re-runs
  // cleanly with the correct state.
  if (res.status === 409 && auth && sameSession()) {
    const detail = (parsed as ApiErrorBody | null)?.detail
    const reason =
      typeof detail === 'object' && detail && !Array.isArray(detail) ? detail.reason : null
    if (reason === 'onboarding_required') {
      useOnboardingStore.getState().reset()
      if (window.location.pathname !== '/onboarding') {
        window.location.assign('/onboarding')
      }
    }
  }

  if (!res.ok) {
    throw new ApiError(res.status, parsed as ApiErrorBody | null)
  }

  // Sliding JWT refresh (bd-9d2): backend stamps a fresh token in
  // X-Refreshed-Token when the current one is near expiry. Swap silently —
  // keeps user_id/displayName, only rotates the token. Same-origin PWA
  // doesn't need CORS expose_headers, but the backend sets it for any
  // cross-origin caller. Read before parsing body to avoid any chance of
  // the header being GC'd between streams.
  if (auth) {
    const refreshed = res.headers.get('X-Refreshed-Token')
    if (refreshed && sameSession()) {
      useAuthStore.getState().setAccessToken(refreshed)
    }
  }

  return parsed as T
}

export const api = {
  get: <T = unknown>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'GET' }),
  post: <T = unknown>(path: string, body?: unknown, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'POST', body }),
  put: <T = unknown>(path: string, body?: unknown, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'PUT', body }),
  del: <T = unknown>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'DELETE' }),
}

export { API_BASE }
